/**
 * Rotas do SIGI.
 *
 * ──────────────────────────────────────────────────────────────────
 * REGRA: TODA rota de módulo DEVE ser envolvida por <RequirePermission>.
 * ──────────────────────────────────────────────────────────────────
 */
import { Suspense, lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";

import { AppLayout } from "@/app/layouts/AppLayout";
import { PublicLayout } from "@/app/layouts/PublicLayout";
import { AuthGuard } from "@/app/guards/AuthGuard";
import { RequirePermission } from "@/app/guards/RequirePermission";
import { SessionGate } from "@/app/guards/SessionGate";
import { PERMISSOES } from "@/hooks/usePermissions";

const LoginPage = lazy(() => import("@/app/pages/LoginPage"));
const AuthCallbackPage = lazy(() => import("@/app/pages/AuthCallbackPage"));
const ForbiddenPage = lazy(() => import("@/app/pages/ForbiddenPage"));
const PoliticaPrivacidadePage = lazy(
  () => import("@/app/pages/PoliticaPrivacidadePage"),
);
const TermosUsoPage = lazy(() => import("@/app/pages/TermosUsoPage"));
const HomePage = lazy(() => import("@/app/pages/HomePage"));
const PerfilPage = lazy(() => import("@/app/pages/PerfilPage"));
const NotFoundPage = lazy(() => import("@/app/pages/NotFoundPage"));

// Módulos extraídos
const AgendamentoDashboardPage = lazy(
  () => import("@/modules/agendamento/pages/AgendamentoDashboardPage"),
);
const AgendamentoCalendarioPage = lazy(
  () => import("@/modules/agendamento/pages/AgendamentoCalendarioPage"),
);
const AgendamentoListaPage = lazy(
  () => import("@/modules/agendamento/pages/AgendamentoListaPage"),
);
const TiposEventoPage = lazy(
  () => import("@/modules/agendamento/pages/TiposEventoPage"),
);
const TiposEventoTutorialPage = lazy(
  () => import("@/modules/agendamento/pages/TiposEventoTutorialPage"),
);
const InspecoesDashboardPage = lazy(
  () => import("@/modules/inspecao/pages/InspecoesDashboardPage"),
);
const InspecoesListPage = lazy(
  () => import("@/modules/inspecao/pages/InspecoesListPage"),
);
const InspecaoDetailPage = lazy(
  () => import("@/modules/inspecao/pages/InspecaoDetailPage"),
);
const InspecaoCreatePage = lazy(
  () => import("@/modules/inspecao/pages/InspecaoCreatePage"),
);
const InspecoesRelatoriosPage = lazy(
  () => import("@/modules/inspecao/pages/InspecoesRelatoriosPage"),
);
const AtividadesPage = lazy(
  () => import("@/modules/inspecao/pages/AtividadesPage"),
);
const NormasPage = lazy(() => import("@/modules/inspecao/pages/NormasPage"));
const TiposInspecaoPage = lazy(
  () => import("@/modules/inspecao/pages/TiposInspecaoPage"),
);
const InspecaoTutorialPage = lazy(
  () => import("@/modules/inspecao/pages/InspecaoTutorialPage"),
);
const ChecklistDashboardPage = lazy(
  () => import("@/modules/checklist/pages/ChecklistDashboardPage"),
);
const ChecklistTemplatesPage = lazy(
  () => import("@/modules/checklist/pages/ChecklistTemplatesPage"),
);
const EpiDashboardPage = lazy(
  () => import("@/modules/epi/pages/EpiDashboardPage"),
);
const PbdocDashboardPage = lazy(
  () => import("@/modules/pbdoc/pages/PbdocDashboardPage"),
);
const SeguirDashboardPage = lazy(
  () => import("@/modules/seguir/pages/SeguirDashboardPage"),
);

// Formulários
const FormulariosDashboardPage = lazy(
  () => import("@/modules/forms/pages/FormulariosDashboardPage"),
);
const FormulariosListPage = lazy(
  () => import("@/modules/forms/pages/FormulariosListPage"),
);
const MascarasFormularioPage = lazy(
  () => import("@/modules/forms/pages/MascarasFormularioPage"),
);
const FormularioEditorPage = lazy(
  () => import("@/modules/forms/pages/FormularioEditorPage"),
);
const FormularioPreviewPage = lazy(
  () => import("@/modules/forms/pages/FormularioPreviewPage"),
);
const FormularioDetalhePage = lazy(
  () => import("@/modules/forms/pages/FormularioDetalhePage"),
);
const FormularioRespostasPage = lazy(
  () => import("@/modules/forms/pages/FormularioRespostasPage"),
);
const FormularioRespostaDetalhePage = lazy(
  () => import("@/modules/forms/pages/FormularioRespostaDetalhePage"),
);
const FormPublicoPage = lazy(
  () => import("@/modules/forms/pages/FormPublicoPage"),
);
const FormulariosPublicosPage = lazy(
  () => import("@/modules/forms/pages/FormulariosPublicosPage"),
);
const AcompanhamentoPublicoPage = lazy(
  () => import("@/modules/forms/pages/AcompanhamentoPublicoPage"),
);
const CancelamentoNotificacaoPage = lazy(
  () => import("@/modules/forms/pages/CancelamentoNotificacaoPage"),
);
const ValidacaoDocumentoPage = lazy(
  () => import("@/modules/forms/pages/ValidacaoDocumentoPage"),
);

// Almoxarifado
const AlmoxarifadoDashboardPage = lazy(
  () => import("@/modules/almoxarifado/pages/AlmoxarifadoDashboardPage"),
);
const CategoriasPage = lazy(
  () => import("@/modules/almoxarifado/pages/CategoriasPage"),
);
const AlmoxEquipamentosPage = lazy(
  () => import("@/modules/almoxarifado/pages/EquipamentosPage"),
);
const EstoquePage = lazy(
  () => import("@/modules/almoxarifado/pages/EstoquePage"),
);
const SolicitacoesPage = lazy(
  () => import("@/modules/almoxarifado/pages/SolicitacoesPage"),
);
const AlmoxRelatoriosPage = lazy(
  () => import("@/modules/almoxarifado/pages/RelatoriosPage"),
);

// Administração (SIGI_ADMIN)
const AdminGruposPage = lazy(
  () => import("@/modules/admin/pages/AdminGruposPage"),
);
const AdminUsuariosPage = lazy(
  () => import("@/modules/admin/pages/AdminUsuariosPage"),
);
const AdminSessoesPage = lazy(
  () => import("@/modules/admin/pages/AdminSessoesPage"),
);
const AdminConfiguracoesPage = lazy(
  () => import("@/modules/admin/pages/AdminConfiguracoesPage"),
);
const AdminPbdocPage = lazy(
  () => import("@/modules/admin/pages/AdminPbdocPage"),
);
const DiagnosticoPage = lazy(
  () => import("@/modules/admin/pages/DiagnosticoPage"),
);

// Cadastro (localidades e cidades)
const LocalidadesListPage = lazy(
  () => import("@/modules/cadastro/pages/LocalidadesListPage"),
);
const LocalidadeFormPage = lazy(
  () => import("@/modules/cadastro/pages/LocalidadeFormPage"),
);
const TiposLocalidadePage = lazy(
  () => import("@/modules/cadastro/pages/TiposLocalidadePage"),
);
const CidadesPage = lazy(() => import("@/modules/cadastro/pages/CidadesPage"));
const EmpresasListPage = lazy(
  () => import("@/modules/cadastro/pages/EmpresasListPage"),
);
const EmpresaFormPage = lazy(
  () => import("@/modules/cadastro/pages/EmpresaFormPage"),
);
const DadosInstitucionaisPage = lazy(
  () => import("@/modules/cadastro/pages/DadosInstitucionaisPage"),
);
const ExpedientesPage = lazy(
  () => import("@/modules/cadastro/pages/ExpedientesPage"),
);
const DocumentosLegaisPage = lazy(
  () => import("@/modules/cadastro/pages/DocumentosLegaisPage"),
);

// RH (funcionários, setores e cargos)
const FuncionariosPage = lazy(
  () => import("@/modules/rh/pages/FuncionariosPage"),
);
const FuncionarioFormPage = lazy(
  () => import("@/modules/rh/pages/FuncionarioFormPage"),
);
const SetoresPage = lazy(() => import("@/modules/rh/pages/SetoresPage"));
const CargosPage = lazy(() => import("@/modules/rh/pages/CargosPage"));
const RegimesTrabalhoPage = lazy(
  () => import("@/modules/rh/pages/RegimesTrabalhoPage"),
);

// Incidentes (eventos adversos reais, investigação e CAT)
const IncidentesListPage = lazy(
  () => import("@/modules/incidente/pages/IncidentesListPage"),
);
const IncidentesOverviewPage = lazy(
  () => import("@/modules/incidente/pages/IncidentesOverviewPage"),
);
const IncidentesRelatoriosPage = lazy(
  () => import("@/modules/incidente/pages/IncidentesRelatoriosPage"),
);
const IncidenteFormPage = lazy(
  () => import("@/modules/incidente/pages/IncidenteFormPage"),
);
const IncidenteDetailPage = lazy(
  () => import("@/modules/incidente/pages/IncidenteDetailPage"),
);
const TiposIncidentePage = lazy(
  () => import("@/modules/incidente/pages/TiposIncidentePage"),
);
const TiposDocumentoIncidentePage = lazy(
  () => import("@/modules/incidente/pages/TiposDocumentoIncidentePage"),
);

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <Suspense>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    path: "/auth/callback",
    element: (
      <Suspense>
        <AuthCallbackPage />
      </Suspense>
    ),
  },
  {
    path: "/403",
    element: (
      <Suspense>
        <ForbiddenPage />
      </Suspense>
    ),
  },
  {
    element: <PublicLayout />,
    children: [
      {
        path: "/termos",
        element: (
          <Suspense>
            <TermosUsoPage />
          </Suspense>
        ),
      },
      {
        path: "/privacidade",
        element: (
          <Suspense>
            <PoliticaPrivacidadePage />
          </Suspense>
        ),
      },
      {
        path: "/f/:slug",
        element: (
          <Suspense>
            <FormPublicoPage />
          </Suspense>
        ),
      },
      {
        path: "/formularios-publicos",
        element: (
          <Suspense>
            <FormulariosPublicosPage />
          </Suspense>
        ),
      },
      {
        path: "/acompanhar",
        element: (
          <Suspense>
            <AcompanhamentoPublicoPage />
          </Suspense>
        ),
      },
      {
        path: "/acompanhar/cancelar",
        element: (
          <Suspense>
            <CancelamentoNotificacaoPage />
          </Suspense>
        ),
      },
      {
        path: "/validar",
        element: (
          <Suspense>
            <ValidacaoDocumentoPage />
          </Suspense>
        ),
      },
      {
        path: "/validar/:codigo",
        element: (
          <Suspense>
            <ValidacaoDocumentoPage />
          </Suspense>
        ),
      },
      // Aliases legíveis (links antigos/documentos) → rotas canônicas.
      { path: "/termos-de-uso", element: <Navigate to="/termos" replace /> },
      {
        path: "/politica-de-privacidade",
        element: <Navigate to="/privacidade" replace />,
      },
    ],
  },
  {
    element: (
      <SessionGate>
        <AuthGuard />
      </SessionGate>
    ),
    children: [
      {
        element: <AppLayout />,
        children: [
          // Home: central de módulos (cada módulo tem a própria dashboard).
          {
            index: true,
            element: (
              <Suspense>
                <HomePage />
              </Suspense>
            ),
          },

          // Alias legado: /modulos cai na home.
          { path: "modulos", element: <Navigate to="/" replace /> },

          // ── Conta do usuário (qualquer sessão autenticada) ──
          {
            path: "perfil",
            element: (
              <Suspense>
                <PerfilPage />
              </Suspense>
            ),
          },

          // ── Inspeções ──
          {
            element: (
              <RequirePermission any={[PERMISSOES.inspecao.consultar]} />
            ),
            children: [
              {
                path: "inspecoes",
                element: (
                  <Suspense>
                    <InspecoesDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/lista",
                element: (
                  <Suspense>
                    <InspecoesListPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/nova",
                element: (
                  <Suspense>
                    <InspecaoCreatePage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/relatorios",
                element: (
                  <Suspense>
                    <InspecoesRelatoriosPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/atividades",
                element: (
                  <Suspense>
                    <AtividadesPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/normas",
                element: (
                  <Suspense>
                    <NormasPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/tipos",
                element: (
                  <Suspense>
                    <TiposInspecaoPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/tutorial",
                element: (
                  <Suspense>
                    <InspecaoTutorialPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/:id",
                element: (
                  <Suspense>
                    <InspecaoDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "inspecoes/:id/editar",
                element: (
                  <RequirePermission any={[PERMISSOES.inspecao.editar]} />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense>
                        <InspecaoCreatePage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },

          // ── Agendamento ──
          {
            element: (
              <RequirePermission any={[PERMISSOES.agendamento.consultar]} />
            ),
            children: [
              {
                path: "agendamento",
                element: (
                  <Suspense>
                    <AgendamentoDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: "agendamento/calendario",
                element: (
                  <Suspense>
                    <AgendamentoCalendarioPage />
                  </Suspense>
                ),
              },
              {
                path: "agendamento/lista",
                element: (
                  <Suspense>
                    <AgendamentoListaPage />
                  </Suspense>
                ),
              },
              {
                path: "agendamento/tipos-evento",
                element: (
                  <Suspense>
                    <TiposEventoPage />
                  </Suspense>
                ),
              },
              {
                path: "agendamento/tipos-evento/guia",
                element: (
                  <Suspense>
                    <TiposEventoTutorialPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Checklists ──
          {
            element: (
              <RequirePermission any={[PERMISSOES.checklist.consultar]} />
            ),
            children: [
              {
                path: "checklists",
                element: (
                  <Suspense>
                    <ChecklistDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: "checklists/templates",
                element: (
                  <Suspense>
                    <ChecklistTemplatesPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Formulários ──
          {
            element: <RequirePermission any={[PERMISSOES.forms.consultar]} />,
            children: [
              {
                path: "formularios",
                element: (
                  <Suspense>
                    <FormulariosDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: "formularios/lista",
                element: (
                  <Suspense>
                    <FormulariosListPage />
                  </Suspense>
                ),
              },
              {
                path: "formularios/mascaras",
                element: (
                  <Suspense>
                    <MascarasFormularioPage />
                  </Suspense>
                ),
              },
              {
                path: "formularios/novo",
                element: <RequirePermission any={[PERMISSOES.forms.criar]} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense>
                        <FormularioEditorPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "formularios/:id",
                element: (
                  <Suspense>
                    <FormularioDetalhePage />
                  </Suspense>
                ),
              },
              {
                path: "formularios/:id/preview",
                element: (
                  <Suspense>
                    <FormularioPreviewPage />
                  </Suspense>
                ),
              },
              {
                path: "formularios/:id/editar",
                element: <RequirePermission any={[PERMISSOES.forms.editar]} />,
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense>
                        <FormularioEditorPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "formularios/:id/respostas",
                element: (
                  <Suspense>
                    <FormularioRespostasPage />
                  </Suspense>
                ),
              },
              {
                path: "formularios/respostas/:id",
                element: (
                  <Suspense>
                    <FormularioRespostaDetalhePage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── EPIs ──
          {
            element: <RequirePermission any={[PERMISSOES.epi.consultar]} />,
            children: [
              {
                path: "epis",
                element: (
                  <Suspense>
                    <EpiDashboardPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── PbDoc ──
          {
            element: <RequirePermission any={[PERMISSOES.pbdoc.consultar]} />,
            children: [
              {
                path: "pbdoc",
                element: (
                  <Suspense>
                    <PbdocDashboardPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Seguir ──
          {
            element: <RequirePermission any={[PERMISSOES.seguir.consultar]} />,
            children: [
              {
                path: "seguir",
                element: (
                  <Suspense>
                    <SeguirDashboardPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Almoxarifado ──
          {
            element: (
              <RequirePermission any={[PERMISSOES.almoxarifado.consultar]} />
            ),
            children: [
              {
                path: "almoxarifado",
                element: (
                  <Suspense>
                    <AlmoxarifadoDashboardPage />
                  </Suspense>
                ),
              },
              {
                path: "almoxarifado/categorias",
                element: (
                  <Suspense>
                    <CategoriasPage />
                  </Suspense>
                ),
              },
              {
                path: "almoxarifado/equipamentos",
                element: (
                  <Suspense>
                    <AlmoxEquipamentosPage />
                  </Suspense>
                ),
              },
              {
                path: "almoxarifado/estoque",
                element: (
                  <Suspense>
                    <EstoquePage />
                  </Suspense>
                ),
              },
              {
                path: "almoxarifado/solicitacoes",
                element: (
                  <Suspense>
                    <SolicitacoesPage />
                  </Suspense>
                ),
              },
              {
                path: "almoxarifado/relatorios",
                element: (
                  <Suspense>
                    <AlmoxRelatoriosPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Cadastro (localidades e cidades) ──
          {
            element: (
              <RequirePermission
                any={[PERMISSOES.cadastro.localidadeConsultar]}
              />
            ),
            children: [
              {
                path: "cadastro/localidades",
                element: (
                  <Suspense>
                    <LocalidadesListPage />
                  </Suspense>
                ),
              },
              {
                path: "cadastro/localidades/nova",
                element: (
                  <Suspense>
                    <LocalidadeFormPage />
                  </Suspense>
                ),
              },
              {
                path: "cadastro/localidades/:id",
                element: (
                  <Suspense>
                    <LocalidadeFormPage />
                  </Suspense>
                ),
              },
              {
                path: "cadastro/tipos-localidade",
                element: (
                  <Suspense>
                    <TiposLocalidadePage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Cidades (catálogo de municípios do endereço) ──
          {
            element: (
              <RequirePermission any={[PERMISSOES.cadastro.cidadeConsultar]} />
            ),
            children: [
              {
                path: "cadastro/cidades",
                element: (
                  <Suspense>
                    <CidadesPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Empresas (contratadas/inspecionadas) ──
          {
            element: (
              <RequirePermission any={[PERMISSOES.cadastro.empresaConsultar]} />
            ),
            children: [
              {
                path: "cadastro/empresas",
                element: (
                  <Suspense>
                    <EmpresasListPage />
                  </Suspense>
                ),
              },
              {
                path: "cadastro/empresas/nova",
                element: (
                  <Suspense>
                    <EmpresaFormPage />
                  </Suspense>
                ),
              },
              {
                path: "cadastro/empresas/:id",
                element: (
                  <Suspense>
                    <EmpresaFormPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Dados institucionais (branding) ──
          {
            element: (
              <RequirePermission
                any={[PERMISSOES.cadastro.institucionalConsultar]}
              />
            ),
            children: [
              {
                path: "cadastro/institucional",
                element: (
                  <Suspense>
                    <DadosInstitucionaisPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Expedientes (horário de funcionamento do órgão) ──
          {
            element: (
              <RequirePermission
                any={[PERMISSOES.cadastro.expedienteConsultar]}
              />
            ),
            children: [
              {
                path: "cadastro/expedientes",
                element: (
                  <Suspense>
                    <ExpedientesPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Documentos legais (Política de Privacidade e Termos de Uso) ──
          {
            element: (
              <RequirePermission
                any={[PERMISSOES.cadastro.documentoConsultar]}
              />
            ),
            children: [
              {
                path: "cadastro/documentos-legais",
                element: (
                  <Suspense>
                    <DocumentosLegaisPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── RH (funcionários e setores) ──
          {
            element: <RequirePermission any={[PERMISSOES.rh.consultar]} />,
            children: [
              {
                path: "rh/funcionarios",
                element: (
                  <Suspense>
                    <FuncionariosPage />
                  </Suspense>
                ),
              },
              {
                path: "rh/funcionarios/novo",
                element: (
                  <Suspense>
                    <FuncionarioFormPage />
                  </Suspense>
                ),
              },
              {
                path: "rh/funcionarios/:id",
                element: (
                  <Suspense>
                    <FuncionarioFormPage />
                  </Suspense>
                ),
              },
              {
                path: "rh/setores",
                element: (
                  <Suspense>
                    <SetoresPage />
                  </Suspense>
                ),
              },
              {
                path: "rh/cargos",
                element: (
                  <Suspense>
                    <CargosPage />
                  </Suspense>
                ),
              },
              {
                path: "rh/regimes-trabalho",
                element: (
                  <Suspense>
                    <RegimesTrabalhoPage />
                  </Suspense>
                ),
              },
            ],
          },

          // ── Incidentes (Gestão de Incidentes e Investigações) ──
          {
            element: (
              <RequirePermission any={[PERMISSOES.incidente.consultar]} />
            ),
            children: [
              {
                path: "incidentes",
                element: (
                  <Suspense>
                    <IncidentesListPage />
                  </Suspense>
                ),
              },
              {
                path: "incidentes/overview",
                element: (
                  <Suspense>
                    <IncidentesOverviewPage />
                  </Suspense>
                ),
              },
              {
                path: "incidentes/relatorios",
                element: (
                  <Suspense>
                    <IncidentesRelatoriosPage />
                  </Suspense>
                ),
              },
              {
                path: "incidentes/novo",
                element: (
                  <RequirePermission any={[PERMISSOES.incidente.criar]} />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense>
                        <IncidenteFormPage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "incidentes/tipos",
                element: (
                  <Suspense>
                    <TiposIncidentePage />
                  </Suspense>
                ),
              },
              {
                path: "incidentes/tipos-documento",
                element: (
                  <RequirePermission any={[PERMISSOES.incidente.editar]} />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense>
                        <TiposDocumentoIncidentePage />
                      </Suspense>
                    ),
                  },
                ],
              },
              {
                path: "incidentes/:id",
                element: (
                  <Suspense>
                    <IncidenteDetailPage />
                  </Suspense>
                ),
              },
              {
                path: "incidentes/:id/editar",
                element: (
                  <RequirePermission any={[PERMISSOES.incidente.editar]} />
                ),
                children: [
                  {
                    index: true,
                    element: (
                      <Suspense>
                        <IncidenteFormPage />
                      </Suspense>
                    ),
                  },
                ],
              },
            ],
          },

          // ── Administração (grupos de trabalho e usuários) ──
          {
            element: <RequirePermission any={[PERMISSOES.admin.gerenciar]} />,
            children: [
              {
                path: "admin",
                element: (
                  <Suspense>
                    <AdminGruposPage />
                  </Suspense>
                ),
              },
              {
                path: "admin/grupos",
                element: (
                  <Suspense>
                    <AdminGruposPage />
                  </Suspense>
                ),
              },
              {
                path: "admin/usuarios",
                element: (
                  <Suspense>
                    <AdminUsuariosPage />
                  </Suspense>
                ),
              },
              {
                path: "admin/sessoes",
                element: (
                  <Suspense>
                    <AdminSessoesPage />
                  </Suspense>
                ),
              },
              {
                path: "admin/configuracoes",
                element: (
                  <Suspense>
                    <AdminConfiguracoesPage />
                  </Suspense>
                ),
              },
              {
                path: "admin/pbdoc",
                element: (
                  <Suspense>
                    <AdminPbdocPage />
                  </Suspense>
                ),
              },
              {
                path: "admin/diagnostico",
                element: (
                  <Suspense>
                    <DiagnosticoPage />
                  </Suspense>
                ),
              },
            ],
          },

          {
            path: "*",
            element: (
              <Suspense>
                <NotFoundPage />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
]);
